import React from 'react';
import classnames from 'classnames';
import { noop, isEmpty, isNil, isRegExp, get } from 'lodash';

// local dependencies
import { Icon } from 'modules/icon-bank';
import { spinner as Spinner } from 'ui/views/generic/spinner';
import { isEnterKey } from 'utils/keyChecker';
import ReactTooltip from 'react-tooltip';

// icon sizes based on widget size
const ICON_SIZE = {
    'default': 18,
    'small': 15,
    'extra-small': 12,
};

const IconView = ( props ) => {

    return (
        <>
            {
                props.loading ? (
                    <div className='ui-search-input__spinner'>
                        <Spinner radius={ ICON_SIZE[ props.size ] }/>
                    </div>
                ) : (
                    <div className={ props.iconClassName }>
                        {
                            isEmpty( props.value ) ? (
                                <div className='ui-search-input__icon__element'>
                                    <Icon name='search' width={ ICON_SIZE[ props.size ] }  height={ ICON_SIZE[ props.size ] } />
                                </div>
                            ) : (
                                <div className='ui-search-input__icon__element' onClick={ () => props.onChange( '' ) }>
                                    <Icon name='close' width={ ICON_SIZE[ props.size ] - 1 }  height={ ICON_SIZE[ props.size ] - 1 } />
                                </div>
                            )
                        }
                    </div>
                )
            }
        </>
    );
};

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { searchInput as SearchInput } from 'ui/views/generic/searchInput';
 *
 * // render
 * <SearchInput propName='propValue'/>
 */
export const searchInput = React.forwardRef( ( props, forwardedRef ) => {

    // widget class name
    const widgetClassName = classnames( 'ui-search-input', props.className, {
        [ `ui-search-input--${ props.size }` ]: ! isEmpty( props.size ),
        'ui-search-input--borderless': props.borderless,
        'ui-search-input--backgroundless': props.backgroundless,
        'ui-search-input--disabled': props.disabled,
    } );

    // icon class name
    const iconClassName = classnames( 'ui-search-input__icon', {
        'ui-search-input__icon--search': isEmpty( props.value ),
        'ui-search-input__icon--close': ! isEmpty( props.value ),
    } );

    return (
        <React.Fragment>
            <div ref={ forwardedRef } className={ widgetClassName } >

                {/* spinner/search/close icon */}
                { ! props.hideIcon && <IconView { ...props } iconClassName={ iconClassName } /> }

                {/* input */}
                        <input
                            className={ ! props.hideIcon ? 'ui-search-input__input' : 'ui-search-input__input-hideIcon'}
                            placeholder={ props.placeholder }
                            value={ props.value }
                            disabled={ props.disabled }
                            onKeyDown={ ( event ) => {
                                const key = get( event, 'code', get( event, 'key', '' ) );

                                if( isEnterKey( key ) ) {
                                    event.preventDefault();
                                    return false;
                                }
                            } }
                            onChange={ ( event ) => {
                                if( ! isNil( props.searchPattern ) && isRegExp( props.searchPattern ) && ! isEmpty( event.target.value ) ) {
                                    if( props.searchPattern.test( event.target.value ) ) {
                                        props.onChange( event.target.value );
                                    }
                                } else {
                                    props.onChange( event.target.value );
                                }
                            } }
                            onFocus={ () => props.onFocus() }
                        />
                        {/* tooltip info icon will come if activateTooltip is true */}

             { props.activateTooltip &&
                <div className='ui-search-input__tooltip'>
                        <ReactTooltip
                                id={ 'tooltip' }
                                aria-haspopup='true'
                                textColor="#000"
                                backgroundColor='#cdd7e1'
                                className="ui-search-input__tooltip__custom"
                                place="bottom"
                        > { props.info } </ReactTooltip>
                        
                        <div data-tip  data-for="tooltip" data-place="bottom">
                            <Icon name='flagError' width= { 17 } height= { 17 } fill= '#003e7b' />
                        </div>
                </div>
            }
            </div>
            
            
           
        </React.Fragment>
    );
} );

// set display name
searchInput.displayName = 'searchInput';

// set default props
searchInput.defaultProps = {
    loading: false,
    activateTooltip: false,
    borderless: true,
    value: '',
    placeholder: 'Type to search...',
    onChange: noop,
    onFocus: noop,
    info: noop,
    size: 'default',
    disabled: false,
    hideIcon: false,
    preventEnterSubmit: true,
};

// prop types
searchInput.propTypes = {};

/******************************************/


/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { searchInputPreloader as SearchInputPreloader } from 'ui/views/generic/searchInput';
 *
 * // render
 * <SearchInputPreloader propName='propValue'/>
 */
export const searchInputPreloader = ( props ) => {

    // widget class name
    const widgetClassName = classnames( 'ui-search-input-preloader', props.className, {
        [ `ui-search-input-preloader--${ props.size }` ]: ! isEmpty( props.size ),
        'ui-search-input-preloader--borderless': props.borderless,
        'ui-search-input-preloader--backgroundless': props.backgroundless,
    } );

    return (
        <div className={ widgetClassName }>
            <div className='ui-search-input-preloader__icon'></div>
            <div className='ui-search-input-preloader__input'></div>
        </div>
    );
};

// set display name
searchInputPreloader.displayName = 'searchInputPreloader';

// set default props
searchInputPreloader.defaultProps = {
    searchPattern: null,
};

// prop types
searchInputPreloader.propTypes = {};
